import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { errorMessage, logError } from "../services/api";
import {
    AlertCircle,
    ArrowLeft,
    Ban,
    ChevronLeft,
    ChevronRight,
    FileSearch,
    Mail,
    Phone,
    User,
    MapPin,
    Clock,
    Calendar,
    Shield,
    RotateCcw,
    Wallet
} from 'lucide-react';
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { isSuperAdmin } from "../utils/role";
import { formatKobo, formatNaira } from "../utils/money";
import {
    TRIP_STATUS_CLASSES,
    buildRidersMap,
    cancellationLine,
    formatLagosDate,
    formatTripDateTime,
    getBookingRecords,
    getPagination,
    getPassengerAmountNaira,
    normalizeTrip,
} from "../utils/tripLog";
import AdjustWalletModal from "../components/AdjustWalletModal";

// Plain admins don't get a customer's contact details; say so rather than "N/A".
const missing = (user) => (user?.role === "customer" && !isSuperAdmin() ? "Hidden" : "N/A");

const RiderDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("profile");
    const [rider, setRider] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [wallet, setWallet] = useState({ status: "loading", balanceKobo: null });
    const [adjusting, setAdjusting] = useState(false);
    const adjustButtonRef = useRef(null);
    // Kept across modal opens so re-sending the same adjustment after a timeout reuses its key.
    const adjustKeysRef = useRef(new Map());

    const loadWallet = useCallback(async () => {
        try {
            const response = await api.get(`/v1/admin/wallets/${id}`);
            const balanceKobo = Number((response.data?.data || response.data)?.balanceKobo);
            // Keep the last good balance on screen if a refresh fails.
            if (Number.isFinite(balanceKobo)) setWallet({ status: "ready", balanceKobo });
            else setWallet((prev) => ({ ...prev, status: prev.status === "ready" ? "ready" : "error" }));
        } catch (err) {
            logError('Unable to fetch customer wallet:', err);
            setWallet((prev) => ({ ...prev, status: prev.status === "ready" ? "ready" : "error" }));
        }
    }, [id]);

    useEffect(() => {
        if (id) loadWallet();
    }, [id, loadWallet]);

    const closeAdjust = useCallback(() => {
        setAdjusting(false);
        adjustButtonRef.current?.focus();
    }, []);

    useEffect(() => {
        const fetchRiderDetails = async () => {
            try {
                setLoading(true);
                // Try to fetch from API
                const response = await api.get(`/v1/admin/users/${id}`);
                console.log('Rider Details Response:', response.data);

                // Handle different response structures
                let riderData = response.data;
                if (response.data.data) riderData = response.data.data;

                setRider(riderData);
                setError(null);
            } catch (err) {
                console.error('Error fetching customer details:', err);
                setError('Failed to load customer details. Please try again.');

                // Fallback: try to find in local storage if API fails
                // This is useful if we navigated from the list and passed data via state/context
                // but here we'll just show the error
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchRiderDetails();
        }
    }, [id]);

    if (loading) {
        return (
            <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
                <Sidebar />
                <div className="flex-1 flex flex-col w-full lg:pl-64">
                    <Topbar />
                    <main className="flex-1 overflow-y-auto p-6 flex items-center justify-center">
                        <div className="text-gray-500">Loading customer details...</div>
                    </main>
                </div>
            </div>
        );
    }

    if (error || !rider) {
        return (
            <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
                <Sidebar />
                <div className="flex-1 flex flex-col w-full lg:pl-64">
                    <Topbar />
                    <main className="flex-1 overflow-y-auto p-6">
                        <div className="text-center py-12">
                            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">
                                {error || 'Customer Not Found'}
                            </h2>
                            <button
                                onClick={() => navigate("/riders")}
                                className="text-[#FF8C00] hover:underline"
                            >
                                Back to Customers
                            </button>
                        </div>
                    </main>
                </div>
            </div>
        );
    }

    const tabItems = [
        { id: "profile", label: "Profile", icon: User },
        { id: "rides", label: "Ride History", icon: MapPin },
    ];

    const displayName = rider.name || `${rider.firstName || ''} ${rider.lastName || ''}`.trim() || 'this customer';

    // Helper to format date
    const formatDate = (dateString) => {
        return formatLagosDate(dateString, 'en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    return (
        <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
            <Sidebar />
            <div className="flex-1 flex flex-col w-full lg:pl-64">
                <Topbar />
                <main className="flex-1 overflow-y-auto p-6">
                    {/* Back Button */}
                    <button
                        onClick={() => navigate('/riders')}
                        className="flex items-center text-gray-600 dark:text-gray-400 hover:text-[#FF8C00] mb-4 transition-colors"
                    >
                        <ArrowLeft size={20} className="mr-2" />
                        <span>Back to Passengers</span>
                    </button>

                    {/* Header with Profile */}
                    <div className="bg-white dark:bg-black rounded-xl shadow-sm p-6 mb-6">
                        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between md:gap-6">
                            {/* Left: Profile Image and Basic Info */}
                            <div className="flex min-w-0 gap-4 flex-1">
                                {/* Profile Image */}
                                <div className="flex-shrink-0">
                                    <div className="w-20 h-20 rounded-full border-2 border-gray-200 dark:border-gray-700 overflow-hidden">
                                        {rider.profileImage ? (
                                            <img
                                                src={rider.profileImage}
                                                alt={rider.name}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full bg-gray-200 dark:bg-gray-800 flex items-center justify-center text-gray-500 text-2xl font-bold">
                                                {(rider.name || rider.firstName || 'U').charAt(0).toUpperCase()}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Rider Info */}
                                <div className="flex-1">
                                    <h1 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                                        {rider.name || `${rider.firstName || ''} ${rider.lastName || ''}`.trim() || 'Unnamed Customer'}
                                    </h1>
                                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-1">
                                        <Mail size={14} />
                                        <span>{rider.email || missing(rider)}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                        <Phone size={14} />
                                        <span>{rider.phone || rider.phoneNumber || missing(rider)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Right: Wallet and Status */}
                            <div className="flex flex-wrap gap-4">
                                <div className="bg-[#FFF4E6] dark:bg-orange-900/20 rounded-lg px-4 py-3 min-w-[160px]">
                                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1 flex items-center gap-1">
                                        <Wallet size={12} />
                                        Wallet balance
                                    </p>
                                    {wallet.status === "ready" ? (
                                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                            {formatKobo(wallet.balanceKobo)}
                                        </p>
                                    ) : wallet.status === "error" ? (
                                        <p className="text-sm text-red-600 dark:text-red-400">
                                            {"Couldn't load. "}
                                            <button type="button" onClick={loadWallet} className="underline font-medium">
                                                Retry
                                            </button>
                                        </p>
                                    ) : (
                                        <p className="text-sm text-gray-500">Loading…</p>
                                    )}
                                    {isSuperAdmin() && (
                                        <button
                                            ref={adjustButtonRef}
                                            type="button"
                                            onClick={() => setAdjusting(true)}
                                            disabled={wallet.status !== "ready"}
                                            className="mt-2 text-xs font-semibold text-[#FF8C00] hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
                                        >
                                            Adjust wallet
                                        </button>
                                    )}
                                </div>
                                <div className="bg-[#FFF4E6] dark:bg-orange-900/20 rounded-lg px-4 py-3 min-w-[120px]">
                                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Status</p>
                                    <div className="flex items-center gap-2">
                                        <div className={`w-2 h-2 rounded-full ${(rider.status === 'active' || rider.isActive) ? 'bg-green-500' : 'bg-red-500'
                                            }`}></div>
                                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                            {(rider.status || (rider.isActive ? 'Active' : 'Inactive')).toUpperCase()}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Content with Left Tab Navigation */}
                    <div className="flex flex-col md:flex-row gap-6">
                        {/* Left Tab Navigation */}
                        <div className="w-full md:w-64 flex-shrink-0">
                            <div className="bg-white dark:bg-black rounded-xl shadow-sm p-2">
                                <nav className="space-y-1">
                                    {tabItems.map((item) => {
                                        const Icon = item.icon;
                                        return (
                                            <button
                                                key={item.id}
                                                onClick={() => setActiveTab(item.id)}
                                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition ${activeTab === item.id
                                                        ? "bg-[#FF8C00] text-black"
                                                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                                                    }`}
                                            >
                                                <Icon size={18} />
                                                {item.label}
                                            </button>
                                        );
                                    })}
                                </nav>
                            </div>
                        </div>

                        {/* Right Content Area */}
                        <div className="flex-1 min-w-0">
                            {activeTab === "profile" && (
                                <div className="space-y-6">
                                    {/* Personal Information */}
                                    <div className="bg-white dark:bg-black rounded-xl shadow-sm p-6">
                                        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
                                            Personal Information
                                        </h3>
                                        <div className="space-y-3">
                                            <DetailRow label="Full Name" value={rider.name || `${rider.firstName || ''} ${rider.lastName || ''}`} />
                                            <DetailRow label="Email" value={rider.email || missing(rider)} />
                                            <DetailRow label="Phone Number" value={rider.phone || rider.phoneNumber || missing(rider)} />
                                            <DetailRow label="User ID" value={rider.id || rider._id} />
                                            <DetailRow label="Joined Date" value={formatDate(rider.createdAt || rider.dateJoined)} />
                                        </div>
                                    </div>

                                    {/* Account Status */}
                                    <div className="bg-white dark:bg-black rounded-xl shadow-sm p-6">
                                        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
                                            Account Status
                                        </h3>
                                        <div className="space-y-3">
                                            <DetailRow label="Email Verified" value={rider.isEmailVerified ? 'Yes' : 'No'} />
                                            <DetailRow label="Phone Verified" value={rider.isPhoneVerified ? 'Yes' : 'No'} />
                                            <DetailRow label="Role" value={rider.role || 'User'} />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === "rides" && <RideHistory key={id} passengerId={id} />}
                        </div>
                    </div>
                </main>
            </div>
            {adjusting && (
                <AdjustWalletModal
                    userId={id}
                    name={displayName}
                    balanceKobo={wallet.balanceKobo}
                    adjustKeys={adjustKeysRef.current}
                    onClose={closeAdjust}
                    onAdjusted={loadWallet}
                />
            )}
        </div>
    );
};

const HISTORY_PAGE_SIZE = 20;

const bookingPassengerId = (booking) => {
    const value = booking?.passengerId;
    return value && typeof value === "object" ? String(value._id || value.id || "") : String(value || "");
};

// The paid amount once a ride is done, the amount due (after any promo) while it runs, nothing
// for a ride that ended without a charge (settlement never ran, so there is no amount to show).
const describeAmount = (booking, status) => {
    if (status === "cancelled" || status === "expired") {
        return typeof booking.amountPaidNgn === "number" ? { text: formatNaira(booking.amountPaidNgn) } : null;
    }
    const amount = getPassengerAmountNaira(booking, status);
    if (amount === null) return null;
    return status === "completed" ? { text: formatNaira(amount) } : { text: formatNaira(amount), hint: "quoted" };
};

// A passenger's bookings, newest first, 20 a page.
const RideHistory = ({ passengerId }) => {
    const [page, setPage] = useState(1);
    const [state, setState] = useState({ status: "loading", rows: [], pagination: null, error: null });
    const [retryToken, setRetryToken] = useState(0);
    // Rider names come from the rider directory; a failure there only costs the names.
    const ridersMapRef = useRef(null);

    useEffect(() => {
        let live = true;
        setState((prev) => ({ ...prev, status: "loading", error: null }));

        const loadRiders = ridersMapRef.current
            ? Promise.resolve(ridersMapRef.current)
            : api
                  .get("/v1/admin/riders-basic")
                  .then((res) => {
                      ridersMapRef.current = buildRidersMap(res.data?.data);
                      return ridersMapRef.current;
                  })
                  .catch((err) => {
                      logError("Unable to load rider names for ride history:", err);
                      return null;
                  });

        Promise.all([
            api.get("/v1/admin/bookings", {
                params: { passengerId, page, limit: HISTORY_PAGE_SIZE },
            }),
            loadRiders,
        ])
            .then(([res, ridersMap]) => {
                if (!live) return;
                const records = getBookingRecords(res.data);
                // A server that ignored the filter would list other people's rides as this passenger's.
                if (records.some((b) => bookingPassengerId(b) && bookingPassengerId(b) !== String(passengerId))) {
                    throw new Error("The server returned rides for other passengers.");
                }
                const rows = records
                    .map((booking) => {
                        const trip = normalizeTrip(booking, ridersMap);
                        const hasRider = Boolean(booking.acceptedBy);
                        return {
                            ...trip,
                            riderLabel: hasRider ? trip.riderName : "No rider",
                            amount: describeAmount(booking, trip.status),
                            cancellationText: cancellationLine(booking),
                        };
                    })
                    .sort((a, b) => new Date(b.requestedAt) - new Date(a.requestedAt));
                setState({ status: "ready", rows, pagination: getPagination(res.data, page, HISTORY_PAGE_SIZE), error: null });
            })
            .catch((err) => {
                if (!live) return;
                logError("Unable to load ride history:", err);
                setState((prev) => ({
                    ...prev,
                    status: "error",
                    error: errorMessage(err, err?.message || "Couldn't load this passenger's rides."),
                }));
            });

        return () => {
            live = false;
        };
    }, [passengerId, page, retryToken]);

    const { status, rows, pagination, error } = state;
    const showPager = status !== "error" && pagination && (pagination.hasNext || pagination.hasPrev);

    return (
        <div className="bg-white dark:bg-black rounded-xl shadow-sm">
            <div className="flex items-center justify-between gap-3 p-4 sm:p-6 pb-3 sm:pb-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Ride History</h3>
                {status === "ready" && pagination?.total ? (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                        {pagination.total.toLocaleString()} ride{pagination.total === 1 ? "" : "s"}
                    </span>
                ) : null}
            </div>

            {status === "error" ? (
                <div role="alert" className="mx-4 sm:mx-6 mb-6 flex flex-col gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
                        <span>
                            {"Couldn't load this passenger's rides. "}
                            <span className="text-red-700/80 dark:text-red-300/80">{error}</span>
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={() => setRetryToken((n) => n + 1)}
                        className="inline-flex w-fit items-center gap-2 rounded-md border border-red-300 px-3 py-1.5 font-medium transition hover:bg-red-100 dark:border-red-800 dark:hover:bg-red-900/40"
                    >
                        <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                        Retry
                    </button>
                </div>
            ) : status === "loading" ? (
                <ul aria-label="Loading ride history" className="divide-y divide-gray-100 dark:divide-gray-800 border-t border-gray-100 dark:border-gray-800">
                    {Array.from({ length: 5 }, (_, i) => (
                        <li key={i} className="animate-pulse px-4 sm:px-6 py-4 space-y-2">
                            <div className="h-3 w-40 rounded bg-gray-200 dark:bg-gray-700" />
                            <div className="h-3 w-64 max-w-full rounded bg-gray-200 dark:bg-gray-700" />
                            <div className="h-3 w-32 rounded bg-gray-200 dark:bg-gray-700" />
                        </li>
                    ))}
                </ul>
            ) : rows.length === 0 ? (
                <div className="text-center px-6 pb-10 pt-6 text-gray-500 dark:text-gray-400">
                    <MapPin className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600 mb-2" />
                    <p className="font-medium text-gray-700 dark:text-gray-200">No rides yet</p>
                    <p className="text-sm mt-1">Rides this passenger books will show here.</p>
                </div>
            ) : (
                <ul className="divide-y divide-gray-100 dark:divide-gray-800 border-t border-gray-100 dark:border-gray-800">
                    {rows.map((ride) => (
                        <li key={ride.id} className="px-4 sm:px-6 py-3 text-sm">
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                <span className="font-semibold text-gray-900 dark:text-white">
                                    {formatTripDateTime(ride.requestedAt)}
                                </span>
                                <span className="font-mono text-xs text-gray-400" title={ride.id}>
                                    {ride.shortId}
                                </span>
                                <span
                                    className={`ml-auto inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
                                        TRIP_STATUS_CLASSES[ride.status] || TRIP_STATUS_CLASSES.unknown
                                    }`}
                                >
                                    {ride.statusLabel}
                                </span>
                            </div>
                            <p className="mt-1 text-gray-600 dark:text-gray-300 break-words">
                                {ride.pickup} → {ride.dropoff}
                            </p>
                            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                                <span>Rider: {ride.riderLabel}</span>
                                {ride.amount ? (
                                    <span>
                                        <span className="font-semibold text-gray-900 dark:text-white">{ride.amount.text}</span>
                                        {ride.amount.hint ? ` ${ride.amount.hint}` : ""}
                                    </span>
                                ) : null}
                                <Link
                                    to={`/trip-inspector/${ride.id}`}
                                    className="ml-auto inline-flex items-center gap-1 font-medium text-gray-500 hover:text-[#FF8C00] dark:text-gray-400"
                                >
                                    <FileSearch className="h-3.5 w-3.5" aria-hidden="true" />
                                    Inspect
                                </Link>
                            </div>
                            {ride.cancellationText ? (
                                <p className="mt-1 flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                                    <Ban className="mt-0.5 h-3 w-3 shrink-0 text-gray-400" aria-hidden="true" />
                                    <span>{ride.cancellationText}</span>
                                </p>
                            ) : null}
                        </li>
                    ))}
                </ul>
            )}

            {showPager ? (
                <nav
                    aria-label="Ride history pages"
                    className="flex items-center justify-between gap-3 border-t border-gray-100 dark:border-gray-800 px-4 sm:px-6 py-3 text-sm text-gray-500 dark:text-gray-400"
                >
                    <span>
                        Page {pagination.page} of {Math.max(1, pagination.totalPages)}
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={!pagination.hasPrev || status === "loading"}
                            className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-3 py-1.5 font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                        >
                            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                            Previous
                        </button>
                        <button
                            type="button"
                            onClick={() => setPage((p) => p + 1)}
                            disabled={!pagination.hasNext || status === "loading"}
                            className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-3 py-1.5 font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                        >
                            Next
                            <ChevronRight className="h-4 w-4" aria-hidden="true" />
                        </button>
                    </div>
                </nav>
            ) : null}
        </div>
    );
};

// Helper component for detail rows
const DetailRow = ({ label, value }) => {
    return (
        <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800 last:border-0">
            <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
            <span className="text-sm font-medium text-gray-900 dark:text-white">{value || 'N/A'}</span>
        </div>
    );
};

export default RiderDetails;
